/**
 * Zod schema for the `RatingSchemaVersion` persistence row + its dimension-
 * type literal.
 *
 * Mirrors `../types/rating-schema.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE rating_schema_versions` + `functionality.md §6.3` for the
 * dimension types.
 *
 * The `schemaJson` column is JSON text; parsing/validating the embedded
 * schema is the evaluator's job. This schema validates the row shape only.
 */

import { z } from "zod";
import type { RatingDimensionType, RatingSchemaVersion } from "../types/rating-schema.js";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

export const RatingDimensionTypeSchema: z.ZodType<RatingDimensionType> = z.enum([
  "stars",
  "boolean",
  "enum",
  "number",
  "text",
  "coord",
  "duration",
  "photo_ref",
  "artefact_ref",
]);

export const RatingSchemaVersionSchema = z
  .object({
    uuid,
    recipeUuid: uuid,
    schemaJson: z.string(),
    supersedesUuid: uuid.nullable().optional(),
    authorUuid: uuid,
    createdAt: timestamp,
  })
  .strict();

type _Assert =
  z.infer<typeof RatingSchemaVersionSchema> extends RatingSchemaVersion
    ? RatingSchemaVersion extends z.infer<typeof RatingSchemaVersionSchema>
      ? true
      : never
    : never;
export type __RatingSchemaVersionSchemaAssert = _Assert;
